import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOSchema from '../components/SEOSchema';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProgramFramework from '../components/ProgramFramework';
import LeadMagnet from '../components/LeadMagnet';
import AnnouncementBanner from '../components/AnnouncementBanner';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import MobileBottomNav from '../components/MobileBottomNav';
import { CheckCircle, ArrowRight, Stethoscope, Apple, Activity } from 'lucide-react';

const PCOSDietPlanPage = () => {
  const faqs = [
    { q: "Can nutrition help with PMOS symptoms?", a: "Evidence suggests balanced, low-glycemic nutrition combined with lifestyle changes may help support hormonal balance and improve PMOS-related symptoms. Results vary based on individual factors. Continue routine care with your physician." },
    { q: "Is this program medically supervised?", a: "Yes. Your plan is reviewed by healthcare professionals who consider your medical history before designing your nutrition and lifestyle approach." },
    { q: "How long does it take to see changes?", a: "Sustainable changes typically take 8 to 12 weeks. We focus on gradual, lasting habits rather than quick fixes." },
    { q: "Do I need to come in person?", a: "No. The entire program is delivered online, including consultations, plans, and follow-ups." }
  ];

  const inclusions = [
    "Personalized PMOS-aware nutrition plan",
    "Lifestyle and movement guidance",
    "Hormone-supportive food guidance",
    "Regular follow-ups and adjustments",
    "WhatsApp-based ongoing support"
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Doctor-Guided PMOS Nutrition &amp; Lifestyle Support | TCUBE FITNESS™</title>
        <meta name="description" content="Doctor-guided PMOS (previously called PCOS) diet plan and lifestyle support designed to help manage hormonal balance through evidence-informed nutrition. Fully online." />
        <link rel="canonical" href="https://tcubefitness.com/pcos-diet-plan" />
      </Helmet>

      <SEOSchema
        type="service"
        name="Doctor-Guided PMOS Diet & Lifestyle Plan"
        description="Doctor-guided PMOS nutrition and lifestyle support designed to help manage hormonal balance through evidence-informed nutrition, fully online."
        url="/pcos-diet-plan"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'PMOS Diet Plan', path: '/pcos-diet-plan' },
        ]}
      />

      <AnnouncementBanner />
      <Header />

      <main id="main-content" className="pt-32 sm:pt-36 pb-24 lg:pb-16">
        <div className="container mx-auto px-4 sm:px-6">

          {/* Hero */}
          <section className="max-w-4xl mx-auto text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500/10 border border-pink-500/30 rounded-full text-pink-400 text-sm font-medium mb-4">
              <Stethoscope className="w-4 h-4" aria-hidden="true" />
              PMOS Support
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
              Doctor-Guided <span className="bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent">PMOS (previously called PCOS) Nutrition &amp; Lifestyle Support</span>
            </h1>
            <p className="text-slate-300 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
              A medically supervised, evidence-informed approach to help you manage PMOS through nutrition, movement, and lifestyle.
            </p>
            <Link to="/book-consultation?from=pcos-top" className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white px-7 py-3.5 rounded-full font-bold shadow-lg shadow-pink-500/25" data-testid="pcos-cta-top">
              Book My Free Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </section>

          {/* What is PMOS */}
          <section className="max-w-3xl mx-auto mb-16 bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
              <Apple className="w-7 h-7 text-pink-400" aria-hidden="true" /> What is PMOS?
            </h2>
            <p className="text-slate-300 leading-relaxed text-sm sm:text-base mb-3">
              PMOS, clinically known as Polycystic Ovary Syndrome, is a common hormonal condition that may present as irregular cycles, weight changes, acne, hair changes, and metabolic concerns. PMOS varies widely from person to person.
            </p>
            <p className="text-slate-400 leading-relaxed text-sm sm:text-base">
              Always consult your physician for diagnosis and ongoing care. Our role is to provide doctor-guided nutrition and lifestyle support that complements your medical management.
            </p>
          </section>

          {/* Nutrition Impact */}
          <section className="max-w-3xl mx-auto mb-16 bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-3">
              <Activity className="w-7 h-7 text-emerald-400" aria-hidden="true" /> How nutrition may support hormonal balance
            </h2>
            <ul className="space-y-3 text-slate-300 text-sm sm:text-base">
              <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-1" aria-hidden="true" /> Balanced macronutrients to support stable energy levels.</li>
              <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-1" aria-hidden="true" /> Adequate fibre, protein and healthy fats.</li>
              <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-1" aria-hidden="true" /> Mindful approach to refined carbohydrates and added sugars.</li>
              <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-1" aria-hidden="true" /> Movement and sleep guidance as part of a holistic plan.</li>
            </ul>
          </section>

          {/* Program Inclusions */}
          <section className="max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5 text-center">Program Inclusions</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {inclusions.map((it, i) => (
                <div key={i} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 flex items-center gap-3 text-slate-300 text-sm">
                  <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0" aria-hidden="true" />
                  {it}
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5 text-center">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 sm:p-5 group" data-testid={`pcos-faq-${i}`}>
                  <summary className="text-white font-semibold cursor-pointer text-sm sm:text-base">{f.q}</summary>
                  <p className="text-slate-400 text-sm mt-2 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="max-w-3xl mx-auto text-center bg-gradient-to-br from-pink-900/30 to-rose-900/30 border border-pink-500/30 rounded-3xl p-8 sm:p-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Ready to start your PMOS journey?</h2>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">Book a free consultation to discuss whether this program is right for you.</p>
            <Link to="/book-consultation?from=pcos-bottom" className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white px-7 py-3.5 rounded-full font-bold shadow-lg shadow-pink-500/25" data-testid="pcos-cta-bottom">
              Book Free Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </section>

        </div>
      </main>

      <section className="py-12 bg-slate-950">
        <div className="container mx-auto px-4 sm:px-6">
          <LeadMagnet className="max-w-4xl mx-auto" />
        </div>
      </section>
      <ProgramFramework slug="pcos-diet-plan" />
      <Footer />
      <FloatingWhatsApp />
      <MobileBottomNav />
    </div>
  );
};

export default PCOSDietPlanPage;
